import React, { useState, useEffect } from 'react';
import { Modal, Button as AntButton, Spin, Alert, message, Input } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { colors, spacing } from '../design-system/tokens';
import { Typography } from '../design-system/typography';
import { DocumentSelector, DocumentType } from '../common/DocumentSelector';

const { TextArea } = Input;

interface ContractEmailModalProps {
  open: boolean;
  contractId: string;
  contractReference: string;
  audience: 'buyer' | 'supplier';
  onClose: () => void;
  onSuccess?: () => void;
}

export const ContractEmailModal: React.FC<ContractEmailModalProps> = ({
  open,
  contractId,
  contractReference,
  audience,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Simplified email fields - TO and CC only
  const [emailTo, setEmailTo] = useState('');
  const [emailCc, setEmailCc] = useState('');

  // Validation errors
  const [toError, setToError] = useState<string | null>(null);
  const [ccError, setCcError] = useState<string | null>(null);

  // Document selection
  const [selectedDocuments, setSelectedDocuments] = useState<{
    generate: DocumentType[];
    attach: string[];
  }>({
    generate: [],
    attach: []
  });

  // Validate single email
  const validateSingleEmail = (email: string): boolean => {
    if (!email.trim()) return false;

    // Support both formats: email@domain.com OR "Name" <email@domain.com>
    const simpleEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const namedEmailRegex = /^"?([^"]*)"?\s*<([^\s@]+@[^\s@]+\.[^\s@]+)>$/;

    if (simpleEmailRegex.test(email.trim())) {
      return true;
    }

    const namedMatch = email.match(namedEmailRegex);
    return namedMatch !== null;
  };

  // Validate comma-separated email list
  const validateEmailList = (emails: string): { valid: boolean; invalid: string[] } => {
    if (!emails.trim()) {
      return { valid: true, invalid: [] }; // Empty is valid for optional fields
    }

    const emailArray = emails.split(',').map(e => e.trim()).filter(Boolean);
    const invalid: string[] = [];

    for (const email of emailArray) {
      if (!validateSingleEmail(email)) {
        invalid.push(email);
      }
    }

    return { valid: invalid.length === 0, invalid };
  };

  // Fetch preview when modal opens
  useEffect(() => {
    if (open && contractId) {
      fetchPreview();
    } else {
      // Reset state when modal closes
      setError(null);
      setEmailTo('');
      setEmailCc('');
      setToError(null);
      setCcError(null);
      setSelectedDocuments({ generate: [], attach: [] });
    }
  }, [open, contractId, audience]);

  const fetchPreview = async () => {
    if (!contractId) return;

    setLoading(true);
    setError(null);

    try {
      const data: any = await api.get(
        `/email-drafts/preview?contractId=${contractId}&audience=${audience}`
      );

      // API interceptor returns response.data directly, so data IS the response data
      // Initialize TO and CC from preview
      setEmailTo(data.email?.to || '');
      setEmailCc(data.email?.cc?.join(', ') || '');

      // Pre-select the appropriate contract document
      const documentType: DocumentType = audience === 'buyer' ? 'contract_buyer' : 'contract_seller';
      setSelectedDocuments({
        generate: [documentType],
        attach: []
      });
    } catch (err: any) {
      console.error('Failed to fetch contract email preview:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load recipient information';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDraft = async () => {
    if (!contractId) return;

    // Validate TO field
    if (!emailTo.trim()) {
      setToError('Recipient email is required');
      return;
    }

    const toValidation = validateEmailList(emailTo);
    if (!toValidation.valid) {
      setToError(`Invalid email(s): ${toValidation.invalid.join(', ')}`);
      return;
    }

    // Validate CC field if provided
    if (emailCc.trim()) {
      const ccValidation = validateEmailList(emailCc);
      if (!ccValidation.valid) {
        setCcError(`Invalid email(s): ${ccValidation.invalid.join(', ')}`);
        return;
      }
    }

    setCreatingDraft(true);

    try {
      // Prepare request body - backend will auto-generate subject/body based on audience
      const requestBody: any = {
        emailTo: emailTo.split(',').map(e => e.trim()).filter(Boolean),
        entityType: 'contract',
        entityId: contractId,
        contractAudience: audience // Tell backend which template to use
      };

      // Add CC if provided
      if (emailCc.trim()) {
        requestBody.emailCc = emailCc.split(',').map(e => e.trim()).filter(Boolean);
      }

      // Add document generation requests
      if (selectedDocuments.generate.length > 0) {
        requestBody.generateDocuments = selectedDocuments.generate.map(type => ({
          type,
          entityId: contractId
        }));
      }

      // Add existing document attachments
      if (selectedDocuments.attach.length > 0) {
        requestBody.attachExistingDocuments = selectedDocuments.attach;
      }

      // Call API to create Gmail draft
      const response: any = await api.post('/email-drafts/create', requestBody);

      if (response.success) {
        message.success('Gmail draft created successfully!');

        // Open Gmail draft in new tab
        if (response.draftUrl) {
          window.open(response.draftUrl, '_blank');
        }

        onSuccess?.();
        onClose();
      } else {
        message.error(response.error || 'Failed to create Gmail draft');
      }
    } catch (err: any) {
      console.error('Failed to create Gmail draft:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to create Gmail draft';
      message.error(errorMessage);
    } finally {
      setCreatingDraft(false);
    }
  };

  const audienceLabel = audience === 'buyer' ? 'Buyer' : 'Supplier';

  return (
    <Modal
      title={`${audienceLabel} Contract Email - ${contractReference}`}
      open={open}
      onCancel={onClose}
      width={900}
      footer={null}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: spacing.xl }}>
          <Spin size="large" />
          <div style={{ marginTop: spacing.md }}>Loading recipient information...</div>
        </div>
      ) : (
        <>
          {error && (
            <Alert
              type="warning"
              message="Could not load recipient information"
              description={error}
              showIcon
              style={{ marginBottom: spacing.lg }}
            />
          )}

          {/* Simplified form - TO, CC, Documents only */}
          <>
            {/* TO Field */}
            <div style={{ marginBottom: spacing.md }}>
              <Typography
                variant="label"
                as="div"
                style={{ color: colors.text.secondary, marginBottom: spacing.xs }}
              >
                TO: <span style={{ color: colors.status.negative }}>*</span>
              </Typography>
              <TextArea
                size="large"
                placeholder="Enter recipient email(s) - comma-separated for multiple"
                value={emailTo}
                onChange={(e) => {
                  setEmailTo(e.target.value);
                  setToError(null);
                }}
                onBlur={() => {
                  if (emailTo.trim()) {
                    const validation = validateEmailList(emailTo);
                    if (!validation.valid) {
                      setToError(`Invalid email(s): ${validation.invalid.join(', ')}`);
                    }
                  }
                }}
                status={toError ? 'error' : undefined}
                autoSize={{ minRows: 1, maxRows: 3 }}
                style={{ marginBottom: toError ? spacing.xs : 0 }}
              />
              {toError && (
                <div style={{ color: colors.status.negative, fontSize: '12px', marginTop: spacing.xs }}>
                  {toError}
                </div>
              )}
            </div>

            {/* CC Field */}
            <div style={{ marginBottom: spacing.lg }}>
              <Typography
                variant="label"
                as="div"
                style={{ color: colors.text.secondary, marginBottom: spacing.xs }}
              >
                CC:
              </Typography>
              <TextArea
                size="large"
                placeholder="Enter CC email(s) - comma-separated for multiple (optional)"
                value={emailCc}
                onChange={(e) => {
                  setEmailCc(e.target.value);
                  setCcError(null);
                }}
                onBlur={() => {
                  if (emailCc.trim()) {
                    const validation = validateEmailList(emailCc);
                    if (!validation.valid) {
                      setCcError(`Invalid email(s): ${validation.invalid.join(', ')}`);
                    }
                  }
                }}
                status={ccError ? 'error' : undefined}
                autoSize={{ minRows: 1, maxRows: 3 }}
                style={{ marginBottom: ccError ? spacing.xs : 0 }}
              />
              {ccError && (
                <div style={{ color: colors.status.negative, fontSize: '12px', marginTop: spacing.xs }}>
                  {ccError}
                </div>
              )}
            </div>

            {/* Document Selector */}
            {contractId && (
              <DocumentSelector
                entityType="contract"
                entityId={contractId}
                allowedGenerateTypes={[
                  audience === 'buyer' ? 'contract_buyer' : 'contract_seller'
                ]}
                onSelectionChange={setSelectedDocuments}
                defaultSelection={{
                  generate: [audience === 'buyer' ? 'contract_buyer' : 'contract_seller'],
                  attach: []
                }}
              />
            )}

            {/* Info text */}
            <div style={{
              marginBottom: spacing.lg,
              padding: spacing.md,
              backgroundColor: colors.background.secondary,
              borderRadius: '4px',
              fontSize: '13px',
              color: colors.text.secondary
            }}>
              A Gmail draft will be created with the {audienceLabel.toLowerCase()} contract email and selected documents.
              You can edit the subject and body in Gmail before sending.
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: spacing.md, justifyContent: 'flex-end' }}>
              <AntButton onClick={onClose} size="large">
                Cancel
              </AntButton>

              <AntButton
                type="primary"
                icon={<MailOutlined />}
                onClick={handleCreateDraft}
                loading={creatingDraft}
                size="large"
                disabled={!emailTo.trim()}
                style={{
                  backgroundColor: colors.status.positive,
                  borderColor: colors.status.positive,
                }}
              >
                {creatingDraft ? 'Creating Draft...' : 'Create Draft in Gmail'}
              </AntButton>
            </div>
          </>
        </>
      )}
    </Modal>
  );
};

export default ContractEmailModal;
